import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
} from 'recharts';
import { gexService, GEXData } from '../../services/gexService';
import { fmt } from '../../utils/formatters';

interface GexChartProps {
  ticker: string;
  /** Override spot price (e.g. from live polling). Falls back to API value. */
  spotPriceOverride?: number | null;
  /** Chart height in px. Default 500. */
  height?: number;
  /** Show the summary stat cards (spot, gamma flip, max pain). Default true. */
  showStats?: boolean;
}

const formatGEX = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return value.toFixed(0);
};

const fmtPrice = (v: number): string => {
  const r = Math.round(v * 10) / 10;
  return r % 1 === 0 ? r.toFixed(0) : r.toFixed(1);
};

const GexChart: React.FC<GexChartProps> = ({
  ticker,
  spotPriceOverride,
  height = 500,
  showStats = true,
}) => {
  const theme = useTheme();
  const [data, setData] = useState<GEXData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedExpiry, setSelectedExpiry] = useState<string>('');
  const [rangePct, setRangePct] = useState<number>(15);

  const fetchGEX = useCallback(async (t: string, expiryMax?: string, pct?: number) => {
    if (!t.trim()) return;
    setLoading(true);
    try {
      const result = await gexService.getGEX(t.trim().toUpperCase(), expiryMax, pct);
      setData(result);
      if (!expiryMax) setSelectedExpiry(result.expiry_max);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ticker) {
      setSelectedExpiry('');
      setRangePct(15);
      fetchGEX(ticker);
    }
  }, [ticker, fetchGEX]);

  const handleExpiryChange = (expiry: string) => {
    setSelectedExpiry(expiry);
    fetchGEX(ticker, expiry, rangePct);
  };

  const handleRangeChange = (_: Event, value: number | number[]) => {
    setRangePct(value as number);
  };

  const handleRangeCommit = (_: Event | React.SyntheticEvent, value: number | number[]) => {
    fetchGEX(ticker, selectedExpiry || undefined, value as number);
  };

  if (loading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  if (!data || !data.strikes.length) return null;

  const spot = spotPriceOverride ?? data.spot_price;

  return (
    <>
      {/* Controls: expiry picker */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {data.available_expiries.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Expirations through</InputLabel>
            <Select
              value={selectedExpiry}
              label="Expirations through"
              onChange={(e) => handleExpiryChange(e.target.value)}
            >
              {data.available_expiries.map((exp) => (
                <MenuItem key={exp} value={exp}>{fmt.date(exp)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {loading && <CircularProgress size={20} />}
      </Box>

      {/* Summary stats */}
      {showStats && (
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Spot Price</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {spot ? fmt.currency(spot) : '—'}
            </Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Gamma Flip</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#00bcd4' }}>
              {data.gamma_flip ? fmt.currency(data.gamma_flip) : '—'}
            </Typography>
          </Paper>
          <Paper sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Max Pain ({fmt.shortDate(selectedExpiry)})</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
              {data.max_pain ? fmt.currency(data.max_pain) : '—'}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* GEX Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {data.ticker} — Gamma Exposure by Strike
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart
            data={data.strikes}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            barGap={-20}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="strike" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(v: number) => `$${v}`} tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={formatGEX} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value: number, name: string) => [formatGEX(value), name]}
              labelFormatter={(label: number) => `Strike: $${label}`}
              contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
            />
            <Legend />
            <Bar dataKey="call_gex" name="Call GEX" fill="#1976d2" opacity={0.85} barSize={20} />
            <Bar dataKey="put_gex" name="Put GEX" fill="#e65100" opacity={0.85} barSize={20} />
            <Line dataKey="net_gex" name="Net GEX" type="monotone" stroke="#fdd835" strokeWidth={3} dot={false} />

            {spot && (
              <ReferenceLine x={spot} stroke={theme.palette.text.primary} strokeDasharray="3 3" strokeWidth={2}
                label={{ value: `Spot $${fmtPrice(spot)}`, position: 'insideTopRight', fill: theme.palette.text.primary, fontSize: 11 }} />
            )}
            {data.gamma_flip && (
              <ReferenceLine x={data.gamma_flip} stroke="#00bcd4" strokeDasharray="12 6" strokeWidth={3}
                label={{ value: `Flip $${fmtPrice(data.gamma_flip)}`, position: 'insideTopLeft', fill: '#00bcd4', fontSize: 11 }} />
            )}
            {data.max_pain && (
              <ReferenceLine x={data.max_pain} stroke="#9c27b0" strokeDasharray="8 4" strokeWidth={2}
                label={{ value: `MaxPain $${fmtPrice(data.max_pain)}`, position: 'insideBottomRight', fill: '#9c27b0', fontSize: 11 }} />
            )}
            <ReferenceLine y={0} stroke={theme.palette.divider} />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Strike range slider + legend */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ flex: '0 0 auto', minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Legend</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 12, bgcolor: '#1976d2', borderRadius: 0.5, opacity: 0.85 }} />
                <Typography variant="caption">Call GEX — positive dealer gamma (supportive)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 12, bgcolor: '#e65100', borderRadius: 0.5, opacity: 0.85 }} />
                <Typography variant="caption">Put GEX — negative dealer gamma (volatile)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 4, bgcolor: '#fdd835', borderRadius: 0.5 }} />
                <Typography variant="caption">Net GEX — call + put combined</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 0, borderTop: '2px dashed', borderColor: 'text.primary' }} />
                <Typography variant="caption">Spot Price — current underlying price</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 0, borderTop: '3px dashed #00bcd4' }} />
                <Typography variant="caption">Gamma Flip — net GEX crosses zero</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 0, borderTop: '2px dashed #9c27b0' }} />
                <Typography variant="caption">Max Pain — strike with least option value</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ flex: '0 1 400px', minWidth: 250, pl: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Strike Range: &plusmn;{rangePct}% of spot
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Narrow the range to focus on near-the-money strikes, or widen to see more of the chain.
            </Typography>
            <Slider
              value={rangePct}
              onChange={handleRangeChange}
              onChangeCommitted={handleRangeCommit}
              min={5} max={30} step={1}
              marks={[
                { value: 5, label: '±5%' }, { value: 10, label: '±10%' },
                { value: 15, label: '±15%' }, { value: 20, label: '±20%' },
                { value: 25, label: '±25%' }, { value: 30, label: '±30%' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `±${v}%`}
            />
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default GexChart;
